<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: () => false
  },
  rootClass: {
    type: String,
    default: () => ''
  },
  modalClass: {
    type: String,
    default: () => ''
  },
  widthClass: {
    type: String,
    default: () => ''
  },
  headerClass: {
    type: String,
    default: () => ''
  },
  title: {
    type: String,
    default: () => ''
  },
  header: {
    type: Boolean,
    default: () => true
  },
  footer: {
    type: Boolean,
    default: () => false
  },
  backdrop: {
    type: Boolean,
    default: () => false
  },
  animation: {
    type: String,
    default: () => 'fade-scale' // opacity-fade, fade-scale, slide-from-[left/right/top/bottm]
  }
});

const emit = defineEmits(['update:modelValue', 'show', 'hide']);

const showModal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const show = () => {
  showModal.value = true;
};
const hide = () => {
  showModal.value = false;
};
watch(
  () => showModal.value,
  () => {
    showModal.value ? emit('show') : emit('hide');
  }
);

const isOverlayClicked = ref(false);
const onOverlayClick = () => {
  if (!props.backdrop) showModal.value = false;
  else {
    (isOverlayClicked.value = true),
      setTimeout(() => {
        isOverlayClicked.value = false;
      }, 600);
  }
};
</script>

<template>
  <Teleport to="body">
    <div :class="rootClass">
      <div
        v-if="showModal"
        class="custom-modal-overlay"
        @click="onOverlayClick"
      ></div>
      <Transition>
        <div
          v-if="showModal"
          class="border rounded-1 custom-modal d-flex flex-column"
          :class="[
            modalClass ? modalClass : '',
            widthClass ? widthClass : 'col-xl-7 col-md-9 col-12',
            isOverlayClicked ? 'backdrop' : '',
            animation ? animation : ''
          ]"
        >
          <slot
            v-if="header"
            name="header"
          >
            <div
              class="w-100 px-3 py-2 d-flex flex-row justify-content-between align-items-center border-bottom"
              :class="[headerClass ? headerClass : '']"
            >
              <slot name="header_title">
                <div class="text-semi-contrast fw-bold text-20px">
                  {{ title ? title : 'Modal title' }}
                </div>
              </slot>

              <slot name="close_button">
                <feather-icon
                  type="x"
                  size="20"
                  stroke-width="3px"
                  class="cursor-pointer text-semi-contrast close-btn"
                  @click="hide"
                />
              </slot>
            </div>
          </slot>

          <PerfectScrollbar
            :options="{ suppressScrollX: true }"
            class="flex-1"
          >
            <slot></slot>
          </PerfectScrollbar>

          <slot
            v-if="footer"
            name="footer"
          >
            <div class="w-100 border-top px-3 py-2">
              <slot name="footer_content">
                <div>Footer</div>
              </slot>
            </div>
          </slot>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
@import '@/assets/base/_base.scss';

.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  z-index: 1887 !important;
  opacity: 0.1 !important;
}
.custom-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 1890;
  max-height: 80vh;
  transform: translate(-50%, -50%) scale(1);
  background: $bg-card;

  &.backdrop {
    animation: animate-backdrop 0.6s 1;
  }

  .close-btn {
    &:hover {
      transform: scale(1.08);
    }
  }

  &.fade-scale {
    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.75);
    }
    &.v-enter-active,
    &.v-leave-active {
      transition: all 0.25s ease-out;
    }
  }
  &.opacity-fade {
    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
      transform: translate(-50%, -50%) scale(1);
    }
    &.v-enter-active,
    &.v-leave-active {
      transition: all 0.25s ease-out;
    }
  }
  &.slide-from-right {
    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
      transform: translate(-45%, -50%) scale(1);
    }
    &.v-enter-active,
    &.v-leave-active {
      transition: all 0.25s ease-out;
    }
  }
  &.slide-from-left {
    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
      transform: translate(-55%, -50%) scale(1);
    }
    &.v-enter-active,
    &.v-leave-active {
      transition: all 0.25s ease-out;
    }
  }
  &.slide-from-top {
    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
      transform: translate(-50%, -55%) scale(1);
    }
    &.v-enter-active,
    &.v-leave-active {
      transition: all 0.25s ease-out;
    }
  }
  &.slide-from-bottom {
    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
      transform: translate(-50%, -45%) scale(1);
    }
    &.v-enter-active,
    &.v-leave-active {
      transition: all 0.25s ease-out;
    }
  }
}

.dark {
  .confirm-modal-overlay {
    background: $m-color_18;
    opacity: 0.5 !important;
  }

  .custom-modal {
    background: $dark-bg-card;
  }
}

@keyframes animate-backdrop {
  0% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.01);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}
</style>
